import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuTrash2 } from 'react-icons/lu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { db, storage } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import CvDocumentList from './CvDocumentList';
import CvUploadCard from './CvUploadCard';

const FILE_INPUT_ID = 'cv-file-input-inline';

const isAllowedFile = (file) => {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();
  return (
    fileType === 'application/pdf' ||
    fileType === 'application/msword' ||
    fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.pdf') ||
    fileName.endsWith('.doc') ||
    fileName.endsWith('.docx')
  );
};

const clearFileInput = () => {
  const fileInput = document.getElementById(FILE_INPUT_ID);
  if (fileInput) fileInput.value = '';
};

/**
 * "Türkçe CV / İngilizce CV / GCP / IATA" (formerly CVManagementModal, inline
 * mode). One document per type - several for IATA - in CVDocuments + Storage
 * `cv-documents/<type>/`. Upload / list / delete work as before; the list and
 * the upload area sit side by side instead of behind two tabs.
 */
const CvDocuments = ({ employeeId, userData, documentType, allowMultiple = false }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.cv.${key}`, vars);
  const [cvFile, setCVFile] = useState(null);
  const [cvFiles, setCVFiles] = useState([]); // Birden fazla dosya için (IATA)
  const [cvUploading, setCVUploading] = useState(false);
  const [myCVs, setMyCVs] = useState([]);
  const [cvLoading, setCVLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Yükleme bitince liste kısa süre vurgulanır (eski "Dosyalarım" sekmesine geçişin yerine)
  const [justUploaded, setJustUploaded] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Belgeleri getir
  const fetchMyCVs = useCallback(async () => {
    if (!employeeId) return;

    setCVLoading(true);
    try {
      const q = query(
        collection(db, 'CVDocuments'),
        where('employeeId', '==', employeeId),
        where('documentType', '==', documentType)
      );
      const snapshot = await getDocs(q);
      const cvList = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));

      // Manuel sıralama - client-side, yeni dosyalar önce
      cvList.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.uploadedAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.uploadedAt || 0);
        return dateB - dateA;
      });

      setMyCVs(cvList);
    } catch (error) {
      console.error('Belgeler yüklenemedi:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setCVLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, documentType]);

  useEffect(() => {
    if (employeeId) fetchMyCVs();
  }, [employeeId, fetchMyCVs]);

  useEffect(() => {
    if (!justUploaded) return undefined;
    const timer = window.setTimeout(() => setJustUploaded(false), 2200);
    return () => window.clearTimeout(timer);
  }, [justUploaded]);

  // Sürükle bırak
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Yalnızca alandan tamamen çıkıldığında
    if (e.currentTarget === e.target) setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Dosya türü ve boyut kontrolü
    const validFiles = files.filter((file) => {
      if (!isAllowedFile(file)) {
        toast.error(label('toast.badType', { name: file.name }));
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(label('toast.tooLarge', { name: file.name }));
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    if (allowMultiple) {
      // IATA için birden fazla dosya
      setCVFiles(validFiles);
    } else {
      // CV/GCP için tek dosya
      if (validFiles.length > 1) toast.warning(label('toast.onlyOne'));
      setCVFile(validFiles[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (allowMultiple) setCVFiles(Array.from(e.target.files));
    else setCVFile(e.target.files[0]);
  };

  // Belge yükleme
  const handleCVUpload = async () => {
    const filesToUpload = allowMultiple && cvFiles.length > 0 ? cvFiles : (cvFile ? [cvFile] : []);

    if (filesToUpload.length === 0) {
      toast.error(label('toast.chooseFile'));
      return;
    }

    // CV-TR, CV-EN ve GCP için tek dosya sınırlaması
    if (!allowMultiple && myCVs.length > 0) {
      toast.error(label('toast.exists', { type: documentType }));
      return;
    }

    for (const file of filesToUpload) {
      if (!isAllowedFile(file)) {
        toast.error(label('toast.badType', { name: file.name }));
        return;
      }
      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(label('toast.tooLarge', { name: file.name }));
        return;
      }
    }

    setCVUploading(true);

    try {
      let uploadedCount = 0;

      // Her dosyayı sırayla yükle
      for (const file of filesToUpload) {
        const timestamp = Date.now() + uploadedCount; // Her dosya için benzersiz timestamp
        const fileExtension = file.name.split('.').pop();
        const storagePath = `cv-documents/${documentType}/${employeeId}-${timestamp}.${fileExtension}`;
        const storageReference = ref(storage, storagePath);

        // Nesne adı zaman damgasından üretildiği için özgün ad contentDisposition
        // ile saklanır. "inline": önizleme tarayıcıda açılır, indirme özgün adı alır.
        await uploadBytes(storageReference, file, {
          contentType: file.type || undefined,
          contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name)}`,
        });
        const downloadURL = await getDownloadURL(storageReference);

        const cvData = {
          employeeId,
          employeeName: `${userData.eName || ''} ${userData.eSurname || ''}`.trim(),
          employeeEmail: userData.eMail || '',
          fileName: file.name,
          fileUrl: downloadURL,
          fileType: fileExtension,
          fileSize: file.size,
          documentType,
          uploadedAt: new Date().toISOString(),
          createdAt: serverTimestamp(),
        };

        await addDoc(collection(db, 'CVDocuments'), cvData);
        uploadedCount++;
      }

      toast.success(label('toast.uploaded', { count: uploadedCount, type: documentType }));

      setCVFile(null);
      setCVFiles([]);
      clearFileInput();

      // Listeyi güncelle ve vurgula
      fetchMyCVs();
      setJustUploaded(true);
    } catch (error) {
      console.error(`❌ ${documentType} yükleme hatası:`, error);
      toast.error(label('toast.uploadError', { type: documentType, message: error.message }));
    } finally {
      setCVUploading(false);
    }
  };

  // Belge silme (önce onay penceresinde sorulur)
  const handleDeleteCV = async (cvId) => {
    try {
      await deleteDoc(doc(db, 'CVDocuments', cvId));
      toast.success(label('toast.deleted', { type: documentType }));
      fetchMyCVs();
    } catch (error) {
      console.error(`❌ ${documentType} silme hatası:`, error);
      toast.error(label('toast.deleteError', { type: documentType }));
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await handleDeleteCV(deleting.id);
    } finally {
      setDeleteBusy(false);
      setDeleting(null);
    }
  };

  return (
    <div className="tw-grid tw-items-start tw-gap-4 lg:tw-grid-cols-2">
      <CvDocumentList
        documentType={documentType}
        allowMultiple={allowMultiple}
        documents={myCVs}
        loading={cvLoading}
        highlight={justUploaded}
        onDelete={setDeleting}
      />
      <CvUploadCard
        documentType={documentType}
        allowMultiple={allowMultiple}
        inputId={FILE_INPUT_ID}
        file={cvFile}
        files={cvFiles}
        hasDocument={myCVs.length > 0}
        uploading={cvUploading}
        dragging={isDragging}
        dragHandlers={{ onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop }}
        onSelect={handleFileSelect}
        onRemoveFile={(index) => {
          const newFiles = cvFiles.filter((_, i) => i !== index);
          setCVFiles(newFiles);
          if (newFiles.length === 0) clearFileInput();
        }}
        onClear={() => {
          setCVFile(null);
          setCVFiles([]);
          clearFileInput();
        }}
        onUpload={handleCVUpload}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        tone="danger"
        icon={LuTrash2}
        title={label('confirmDelete.title')}
        message={label('confirmDelete.message', { name: deleting?.fileName || '' })}
        confirmLabel={label('confirmDelete.confirm')}
        cancelLabel={t('profilePage.common.cancel')}
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

CvDocuments.propTypes = {
  employeeId: PropTypes.string,
  userData: PropTypes.object.isRequired,
  documentType: PropTypes.oneOf(['CV-TR', 'CV-EN', 'GCP', 'IATA']).isRequired,
  allowMultiple: PropTypes.bool,
};

export default CvDocuments;
